/* Scroll reveal: adds .in-view as elements with .reveal enter the viewport.
   Exposes window.initReveal() so the theme editor can re-init after section re-renders.
   The .reveal base state and reduced-motion override live in theme.css.
 */
(function () {
  'use strict';

  function initReveal() {
    const els = document.querySelectorAll('.reveal:not([data-reveal-initialized])');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in-view'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => {
      el.setAttribute('data-reveal-initialized', 'true');
      io.observe(el);
    });
  }

  window.initReveal = initReveal;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }
})();

/* Scroll-driven word reveal for [data-reveal-text] elements.
   Splits text into words, creates ghost/fill spans, animates opacity on scroll.
   Respects prefers-reduced-motion. Exposes window.initTextReveal() for theme editor.
   Based on Magic UI's TextReveal pattern, adapted for Thai (phrase-based splitting).
*/
(function () {
  'use strict';

  function initTextReveal(root) {
    const scope = root || document;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = [];

    scope.querySelectorAll('[data-reveal-text]').forEach(function (el) {
      if (el.dataset.textRevealInitialized === 'true') return;

      const text = el.textContent.replace(/\s+/g, ' ').trim();
      if (!text) return;

      // For Thai: split by spaces (phrases), not individual characters
      const chunks = text.split(' ');

      // Assistive tech reads the heading once via aria-label
      el.setAttribute('aria-label', text);
      el.textContent = '';

      const fills = [];
      chunks.forEach(function (chunk, i) {
        if (i > 0) el.appendChild(document.createTextNode(' '));
        const wrap = document.createElement('span');
        wrap.className = 'rw';
        wrap.setAttribute('aria-hidden', 'true');
        const ghost = document.createElement('span');
        ghost.className = 'rw__ghost';
        ghost.textContent = chunk;
        const fill = document.createElement('span');
        fill.className = 'rw__fill';
        fill.textContent = chunk;
        wrap.appendChild(ghost);
        wrap.appendChild(fill);
        el.appendChild(wrap);
        fills.push(fill);
      });

      if (reduce) {
        fills.forEach(function (f) { f.style.opacity = 1; });
        el.dataset.textRevealInitialized = 'true';
        return;
      }

      el.__fills = fills;
      targets.push(el);
    });

    if (!targets.length) return;

    function paint() {
      const vh = window.innerHeight;
      targets.forEach(function (el) {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        // 0 when heading enters at 82% of viewport, 1 once it clears 45%
        const span = r.height + vh * 0.37;
        const passed = vh * 0.82 - r.top;
        const p = Math.min(1, Math.max(0, passed / span));
        const fills = el.__fills, n = fills.length;
        for (let i = 0; i < n; i++) {
          const start = i / n;
          const end = Math.min(1, start + (1 / n) * 1.8); // overlap softens steps
          const o = (p - start) / (end - start);
          fills[i].style.opacity = Math.min(1, Math.max(0, o));
        }
      });
    }

    let queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; paint(); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    paint();

    // Mark all processed
    targets.forEach(function (el) { el.dataset.textRevealInitialized = 'true'; });
  }

  window.initTextReveal = initTextReveal;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initTextReveal(); });
  } else {
    initTextReveal();
  }
})();
